import React from "react";
import { useMemo, useRef } from "react";
import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import * as d3 from "d3";

import TitleCase from "components/formatting/TitleCase";
import ReusableWordCloud from "./ReusableWordCloud";
import { Word } from "./WordCloudContent";
import { wordCloudDefaultSettings, WordCloudSettingsStateType } from "./WordCloudUtils";

interface WordCloudPreviewProps {
  /**
   * State object represented by series of key value pairs,
   * where the key is a string which represents the title of the
   * updatable settings option.
   */
  settingsState: WordCloudSettingsStateType | {};
}

export const exampleWords = [
  "pineapple", "mango", "strawberry", "banana", "soursop", "starfruit", "blackberry",
  "guava", "pear", "apple", "lime", "dragon fruit", "durian", "tamarind",
  "mangosteen", "orange", "passion fruit", "açaí", "lychee", "sugar apple", "apricot",
  "papaya", "rambutan", "jackfruit", "coconut", "blood orange", "breadfruit", "fig",
  "raspberry", "blueberry", "cherry", "lemon", "peach", "plum", "grape", "longan",
  "kiwi", "pomegranate", "pear", "avocado", "persimmon", "date", "currant",
  "clementine", "gooseberry", "quince", "nectarine", "cacao", "corozo", "sapote"
];

export default function WordCloudPreview(props: WordCloudPreviewProps) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const currentState = {...wordCloudDefaultSettings, ...props.settingsState};
  const previewRef = useRef<HTMLDivElement | null | undefined>();
  const namedColors = theme.colors.namedColors;

  const colorKey = currentState?.colorKey ?? "positivity";
  const colorInterpolate = colorKey === 'positivity' ?
    d3.interpolateRgbBasis([namedColors.negative, namedColors.mixed, namedColors.positive]) :
    d3.interpolateRgbBasis([namedColors.chart1Light, namedColors.chart1, namedColors.chart1Dark]);

  let cloudExampleWords = [];
  const maxWordCount = 100; // For performance issues: don't render more than 100 words in preview
  const wordsToRender = currentState.wordCount > maxWordCount ? maxWordCount : currentState.wordCount;

  // Doubling the example words array to get 100 example words on initial render
  for (let i = 0; i < wordsToRender; i++) {
    cloudExampleWords.push(exampleWords[i % exampleWords.length]);
  }

  let exampleTopics: Word[] = useMemo(() => {
    let innerExampleTopics = cloudExampleWords.map((word, index) => {
      return {
        appearances: Math.floor(Math.random() * 100),
        cluster_id: JSON.stringify(index),
        color: theme.colors.primary.main,
        count: Math.floor(Math.random() * 100),
        distribution: 0.01,
        est_total_appearances: Math.floor(Math.random() * 500),
        est_total_count: Math.floor(Math.random() * 500),
        est_total_negative_count: Math.floor(Math.random() * 500),
        est_total_positive_count: Math.floor(Math.random() * 500),
        label: word,
        language: "en",
        negative_count: Math.floor(Math.random() * 100),
        positive_count: Math.floor(Math.random() * 100),
        positivity: Math.random() * 1,
        query: null,
        relevance: Math.floor(Math.random() * 100),
        sentimental: Math.random() * 1,
        text: word,
        displayText: word,
        velocity: Math.random(),
      }
    });

    const colorValues = innerExampleTopics?.map((data: Word) => data[colorKey]).filter((value: number) => value !== undefined) || [];
    const colorRange = [Math.min(...colorValues), Math.max(...colorValues)];
    const colorScale = d3.scaleLinear().domain(colorRange).range([0.0, 1.0]);

    innerExampleTopics = innerExampleTopics.map(wordObj => {
        return {
          ...wordObj,
          color: wordObj.positivity === null
            ? namedColors.neutral
            : colorInterpolate(colorScale(wordObj[colorKey])),
        };
      });
      return innerExampleTopics;
  }, [cloudExampleWords]);

  const sizeRange = useMemo(() => {
    const values = exampleTopics.map((word: Word) => {
      return word[currentState.dataKey as keyof Word ?? 'relevance'];
    }).filter((value: number) => value !== undefined) || [];

    return [Math.min(...values), Math.max(...values)];
  }, [currentState.dataKey]);

  const fontRange = [32, 200];
  const sizeScale = d3.scaleLinear().domain(sizeRange).range(fontRange);
  
  exampleTopics = useMemo(() => {
    return exampleTopics.map((word: Word) => {
      word.size = sizeScale(word[currentState.dataKey as keyof Word ?? 'relevance']);
      return word;
    });
  }, [sizeScale]);

  return (
    <Stack sx={{ width: isSmallScreen ? "100%" : "50%" }}>
      {currentState.widgetTitle !== 'none' &&
        <Typography
          sx={{ fontSize: "1.2rem", textAlign: `${currentState.widgetTitleAlignment}` }}
          variant="subtitle2">
        <TitleCase text={currentState.widgetTitle} />
        </Typography>}
      <Box ref={previewRef}>
        <ReusableWordCloud
          settings={currentState}
          words={exampleTopics} />
      </Box>
    </Stack>
  )
}
